import { faBarsFilter } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ZonedDateTime } from '@js-joda/core';
import isEqual from 'lodash/isEqual';
import React from 'react';
import styled from 'styled-components';
import {
  defaultClassesDateRange,
  useClassesQueryVariables,
} from '../../../hooks/useQueryParams';
import useTracking from '../../../hooks/useTracking';
import {
  getLocalTimeUtcEndOfDay,
  getLocalTimeUtcStartOfDay,
} from '../../../utils/date';
import {
  EventCategories,
  EventLabels,
  EventNames,
  EventProfileCardNames,
  EventSubCategories,
} from '../../../utils/trackingEvents';
import Button, { ButtonIconPosition } from '../../reusable/Button';
import { DateRangePicker, ZdtDateRange } from '../../reusable/DateRangePicker';
import { RangeTypeWithEventLabel } from '../../reusable/DateRangePicker/predefinedRanges';

const StyledHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

interface FiltersHeaderProps {
  isExpanded: boolean;
  toggleShowSidebar: () => void;
}
export const FiltersHeader: React.FC<FiltersHeaderProps> = ({
  isExpanded,
  toggleShowSidebar,
}) => {
  const [classesQueryVariables, setClassesQueryVariables] =
    useClassesQueryVariables();
  const { dateRange } = classesQueryVariables;

  const { track } = useTracking();

  // Create custom predefined ranges that respect our date limits (today to 45 days from today)
  const startOfToday = getLocalTimeUtcStartOfDay();
  const endOfToday = getLocalTimeUtcEndOfDay();
  const zdtToJsDate = (zdt: ZonedDateTime) =>
    new Date(zdt.withFixedOffsetZone().toJSON());

  const customPredefinedRanges: RangeTypeWithEventLabel[] = [
    {
      eventlabel: EventLabels.Today,
      label: 'Today',
      value: [zdtToJsDate(startOfToday), zdtToJsDate(endOfToday)],
      placement: 'left',
    },
    {
      eventlabel: EventLabels.Next7days,
      label: 'Next 7 Days',
      value: [zdtToJsDate(startOfToday), zdtToJsDate(endOfToday.plusDays(7))],
      placement: 'left',
    },
    {
      eventlabel: EventLabels.Next30days,
      label: 'Next 30 Days',
      value: [zdtToJsDate(startOfToday), zdtToJsDate(endOfToday.plusDays(30))],
      placement: 'left',
    },
    {
      eventlabel: EventLabels.Next45days,
      label: 'Next 45 Days',
      value: [zdtToJsDate(startOfToday), zdtToJsDate(endOfToday.plusDays(45))],
      placement: 'left',
    },
  ];

  // Create date limit function: today as start limit, 45 days from today as end limit
  const shouldDisableDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of today

    const endOfToday = new Date(today);
    endOfToday.setHours(23, 59, 59, 999);

    const maxDate = new Date(endOfToday);
    maxDate.setDate(endOfToday.getDate() + 45); // 45 days from today

    return date < today || date > maxDate;
  };
  const handleFilterButtonClick = () => {
    const nextStateIsExpanded = !isExpanded;
    track(EventNames.ButtonClick, {
      event_category: EventCategories.Classes,
      event_label: EventLabels.Filter,
      event_subcategory: nextStateIsExpanded
        ? EventSubCategories.Expand
        : EventSubCategories.Collapse,
    });
    toggleShowSidebar();
  };

  const handleDateRangeChange = (
    value: ZdtDateRange,
    buttonLabel?: EventLabels,
  ) => {
    track(EventNames.ButtonClick, {
      card_name: EventProfileCardNames.DateRangeModal,
      event_category: EventCategories.Classes,
      event_label: buttonLabel,
    });

    setClassesQueryVariables({
      ...classesQueryVariables,
      dateRange: {
        start: value.start?.toJSON(),
        end: value.end?.toJSON(),
      },
    });
  };

  const handleDateRangeClear = () => {
    track(EventNames.ButtonClick, {
      card_name: EventProfileCardNames.DateRangeModal,
      event_category: EventCategories.Classes,
      event_label: EventLabels.Reset,
    });
    if (dateRange.start || dateRange.end) {
      setClassesQueryVariables({
        ...classesQueryVariables,
        dateRange: {},
      });
    }
  };

  const handleDateRangeOpen = () => {
    track(EventNames.CalendarIconClick, {
      event_category: EventCategories.Classes,
    });
  };

  return (
    <StyledHeader>
      <LeftContainer>
        <Button
          iconPosition={ButtonIconPosition.Left}
          testId="Button-FilterToggle"
          icon={<FontAwesomeIcon icon={faBarsFilter} />}
          onClick={handleFilterButtonClick}
        >
          Filter
        </Button>
        <DateRangePicker
          value={{
            start: dateRange.start
              ? ZonedDateTime.parse(dateRange.start)
              : null,
            end: dateRange.end ? ZonedDateTime.parse(dateRange.end) : null,
          }}
          clearable={
            !isEqual(dateRange, defaultClassesDateRange) &&
            (Boolean(dateRange.start) || Boolean(dateRange.end))
          }
          onClear={handleDateRangeClear}
          onChange={handleDateRangeChange}
          onOpen={handleDateRangeOpen}
          shouldDisableDate={shouldDisableDate}
          predefinedRanges={customPredefinedRanges}
        />
      </LeftContainer>
    </StyledHeader>
  );
};
